import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Spin, Alert } from 'antd';
import { LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { googleAuth } from '../services/auth';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current || isProcessing) {
      console.log('Auth callback already processed or in progress, skipping');
      return;
    }

    const handleCallback = async () => {
      // Mark as processing to prevent concurrent executions
      setIsProcessing(true);
      hasProcessed.current = true;

      try {
        console.log('Starting auth callback processing:', {
          timestamp: new Date().toISOString(),
          hasToken: searchParams.has('token'),
          hasError: searchParams.has('error')
        });

        // Check for error from backend first
        const errorParam = searchParams.get('error');
        if (errorParam) {
          throw new Error(decodeURIComponent(errorParam));
        }

        const token = searchParams.get('token');

        if (token) {
          console.log('Token received, logging in user');
          await login(token);

          console.log('Login successful, redirecting to dashboard');
          navigate('/dashboard');
        } else {
          throw new Error('No authentication token received from the callback');
        }
      } catch (error: any) {
        console.error('Auth callback error:', {
          message: error.message,
          timestamp: new Date().toISOString()
        });
        setError(error.message);
        
        // Redirect to login with error after a delay to show the error message
        setTimeout(() => {
          navigate(`/login?error=${encodeURIComponent(error.message)}`);
        }, 3000);
      } finally {
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, []); // Remove dependencies to prevent re-runs

  if (error) {
    return (
      <div style={{
        height: '100vh',
        background: 'var(--bg-secondary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}>
        <Card style={{ 
          width: 500, 
          maxWidth: '100%',
          textAlign: 'center'
        }}>
          <div style={{ padding: '24px' }}>
            <WarningOutlined style={{ 
              fontSize: 48, 
              color: 'var(--color-negative)',
              marginBottom: '24px'
            }} />
            
            <div className="text-card-title" style={{ marginBottom: '16px' }}>
              Authentication Failed
            </div>
            
            <Alert 
              message={error}
              type="error"
              style={{ marginBottom: '16px' }}
            />
            
            <div className="text-card-body" style={{ color: 'var(--text-secondary)' }}>
              Redirecting you back to login...
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      height: '100vh',
      background: 'var(--bg-secondary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <Card style={{ 
        width: 400, 
        maxWidth: '100%',
        textAlign: 'center'
      }}>
        <div style={{ padding: '24px' }}>
          <LoadingOutlined style={{ 
            fontSize: 48, 
            color: 'var(--color-positive)',
            marginBottom: '24px'
          }} />
          
          <div className="text-card-title" style={{ marginBottom: '16px' }}>
            Completing Sign In
          </div>
          
          <div className="text-card-body" style={{ color: 'var(--text-secondary)' }}>
            Please wait while we complete your authentication...
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AuthCallback;